import type { ProjectCreator } from "@t3tools/contracts";
import { UserIcon } from "lucide-react";
import { cloneElement, useId, type ComponentProps, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "~/lib/utils";
import {
  ownerAvatarColorClass,
  ownerInitials,
  withDescriptionId,
} from "./ProjectOwnerAvatar.logic";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

const AVATAR_SIZES = {
  default: { circle: "size-5 text-[9px]", icon: "size-3" },
  sm: { circle: "size-4 text-[8px]", icon: "size-2.5" },
} as const;

/** Decorative: `TitleAuthorTooltip` on the surrounding element names the owner. */
export function ProjectOwnerAvatar({
  owner,
  size = "default",
}: {
  owner: ProjectCreator;
  size?: keyof typeof AVATAR_SIZES;
}) {
  const initials = ownerInitials(owner.name);
  const sizes = AVATAR_SIZES[size];
  return (
    <span
      aria-hidden
      className={`inline-flex ${sizes.circle} shrink-0 select-none items-center justify-center rounded-full font-semibold leading-none ${ownerAvatarColorClass(owner.userId)}`}
    >
      {initials ?? <UserIcon className={sizes.icon} />}
    </span>
  );
}

/**
 * Gives `render` a tooltip with `title` in full, `detail` muted in its top-right corner, and, for
 * an author with a name, «Автор: {name}» beneath. The author and detail are also `render`'s
 * accessible description, which leaves its accessible name alone. A tooltip trigger nested in
 * `render`, such as a sidebar row's status dot, holds this tooltip closed while hovered.
 * `trackCursorAxis` places it at the pointer along that axis. A null `title` leaves `render` as it
 * is.
 */
export function TitleAuthorTooltip({
  title,
  author,
  detail = null,
  side,
  align,
  trackCursorAxis,
  popupClassName,
  disabled,
  render,
}: {
  title: string | null;
  /** Null off a team server: authors are named there alone. */
  author: ProjectCreator | null;
  detail?: string | null;
  side: ComponentProps<typeof TooltipPopup>["side"];
  align?: ComponentProps<typeof TooltipPopup>["align"];
  trackCursorAxis?: ComponentProps<typeof Tooltip>["trackCursorAxis"];
  popupClassName?: string;
  disabled?: boolean;
  render: ReactElement<{ "aria-describedby"?: string | undefined }>;
}) {
  const { t } = useTranslation();
  const descriptionId = useId();
  if (title === null) {
    return render;
  }

  const authorLabel = author?.name ? t("common/author_ffl232", { name: author.name }) : null;
  const descriptionLines = [authorLabel, detail].filter((line) => line !== null);
  return (
    <>
      <Tooltip trackCursorAxis={trackCursorAxis}>
        <TooltipTrigger
          disabled={disabled}
          render={
            descriptionLines.length === 0
              ? render
              : cloneElement(render, {
                  "aria-describedby": withDescriptionId(
                    render.props["aria-describedby"],
                    descriptionId,
                  ),
                })
          }
        />
        <TooltipPopup
          side={side}
          align={align}
          className={cn("max-w-80 whitespace-normal leading-tight", popupClassName)}
        >
          <span className="flex flex-col gap-0.5 py-0.5">
            {detail === null ? (
              <span className="wrap-break-word font-semibold">{title}</span>
            ) : (
              <span className="flex items-baseline gap-3">
                <span className="min-w-0 flex-1 wrap-break-word font-semibold">{title}</span>
                <span className="shrink-0 text-muted-foreground tabular-nums">{detail}</span>
              </span>
            )}
            {authorLabel === null ? null : <span className="wrap-break-word">{authorLabel}</span>}
          </span>
        </TooltipPopup>
      </Tooltip>
      {descriptionLines.length === 0 ? null : (
        <span id={descriptionId} hidden>
          {descriptionLines.join(", ")}
        </span>
      )}
    </>
  );
}
